import { KpkApiMedia } from "@keepeek/api-client";
import {
  DataViewKey,
  FetcherMode,
  createBasketBusinessFilter,
  currentUserQuery,
  dataViewElementsSelector,
  dataViewPage,
  dataViewSize,
  dataViewSortSelector,
  elementsSelectionIdState,
  fetcherModeAtom,
  formatFieldsForKpkApi,
  isAuthenticatedSelector,
  serializeComplexeParamsForRecoil,
  downloadManagerDataviewKey,
} from "@keepeek/commons";
import Axios from "axios";
import { selectorFamily } from "recoil";

import { convertToKpkAttachments } from "../../../lib/attachments/attachments-utils";
import logger from "../../../lib/logger-utils";
import { currentBasketIdState } from "../../basket/atoms";
import { activeBusinessFiltersFromURLSelector } from "../../businessFilter/selectors/activeFilters";
import { configSectionComponentBusinessFilterSelector } from "../../config/selectors";
import { businessFiltersConfigurationAdaptor } from "../../config/utils";
import { downloadManagerFetcherSelector } from "../../fetcher/selectors/downloadManager";
import { DownloadManagerAttachmentsByElement } from "../atoms/attachments";
import { downloadManagerFormatsQuery } from "./formats";

export const attachmentsByElementsIdQuerySelector = selectorFamily<
  DownloadManagerAttachmentsByElement[] | null,
  string
>({
  key: "AttachmentsByElementIdQuerySelector",
  get:
    (locale) =>
    async ({ get }) => {
      const isAuthenticated = get(isAuthenticatedSelector);
      const fetcherMode = get(fetcherModeAtom);
      if (!isAuthenticated && !(fetcherMode === FetcherMode.SHARE)) {
        return null;
      }
      const elementsId = get(elementsSelectionIdState);
      const resultLoaded: Array<DownloadManagerAttachmentsByElement> = [];
      const resultUnLoaded: Array<DownloadManagerAttachmentsByElement> = [];
      const result: Array<DownloadManagerAttachmentsByElement> = [];
      if (elementsId) {
        const { postSelection, postSelectionMediasCollection, getSelectionMedias } = get(
          downloadManagerFetcherSelector(fetcherMode),
        );

        const userId = get(currentUserQuery).id;
        const { selectionId } = get(downloadManagerFormatsQuery);
        if (!selectionId) {
          // Create a selection
          const postSelectionQuery = await postSelection({ userId });
          if (!postSelectionQuery) {
            throw new Error("cannot post selection without authenticated user");
          }

          const {
            data: { id: selectionId },
          } = postSelectionQuery;
          try {
            // Add element(s) to the selection
            await postSelectionMediasCollection({
              userId,
              selectionId: selectionId,
              mediaIds: elementsId,
            });
          } catch (error) {
            if (Axios.isAxiosError(error) && error.response?.status !== 500) {
              logger &&
                logger.debug(
                  `Error while adding element to selection ${selectionId} : `,
                  error.response?.data?.message,
                );
              return null;
            } else throw error;
          }
        }

        if (selectionId && elementsId && elementsId.length > 0) {
          let getSelectionMediasQuery;
          try {
            getSelectionMediasQuery = await getSelectionMedias({
              userId: userId,
              selectionId: selectionId,
              fields: formatFieldsForKpkApi(["id", "attachments"]),
              size: elementsId.length,
            });
            if (!getSelectionMediasQuery) {
              throw new Error("cannot get selection without authenticated user");
            }
          } catch (error) {
            if (Axios.isAxiosError(error) && error.response?.status !== 500) {
              logger &&
                logger.debug(
                  `Error while getting medias for selection ${selectionId} :`,
                  error.response?.data?.message,
                );
              return null;
            } else throw error;
          }
          const {
            data: { _embedded },
          } = getSelectionMediasQuery;

          const filtersConfiguration =
            get(configSectionComponentBusinessFilterSelector)?.filters || null;

          if (_embedded?.media) {
            const currentBasket = get(currentBasketIdState);
            const currentDataViewKey = get(downloadManagerDataviewKey);
            if (currentDataViewKey === undefined) {
              return null;
            }
            const isSideBarBasketContext = currentDataViewKey === DataViewKey.BASKET;
            const basketFilter = currentBasket ? [createBasketBusinessFilter(currentBasket)] : [];

            const elements = get(
              dataViewElementsSelector(
                serializeComplexeParamsForRecoil({
                  key: currentDataViewKey,
                  size: get(dataViewSize(currentDataViewKey)),
                  page: get(dataViewPage(currentDataViewKey)),
                  sort: get(dataViewSortSelector(currentDataViewKey)),
                  filtersConfiguration: businessFiltersConfigurationAdaptor(filtersConfiguration),
                  filters: isSideBarBasketContext
                    ? basketFilter
                    : get(activeBusinessFiltersFromURLSelector),
                }),
              ),
            );

            const loadedElementsId: number[] = elements.map((e) => e.id);

            const medias: KpkApiMedia[] = _embedded.media;
            // Order by sort same that in dataview and loaded elements in first
            // TODO : doing a api call advanced search with a selectionId in parameter so we can get all datas for all elements in same api. But that is not possible for the moment.
            for (const id of elementsId) {
              const media: KpkApiMedia | undefined = medias.find((m) => m.id === id);
              if (media) {
                const attachments = convertToKpkAttachments(
                  media._embedded?.["attachment"],
                  locale,
                  true,
                );

                if (media && loadedElementsId.includes(id)) {
                  resultLoaded.push({ elementId: media.id || 0, attachments });
                } else {
                  resultUnLoaded.push({ elementId: media.id || 0, attachments });
                }
              }
            }
          }
        }
      }
      return result.concat(resultLoaded, resultUnLoaded);
    },
});
